import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef, EventEmitter,
  Input,
  OnInit, Output,
  ViewChild
} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {PatientAccountService} from "../../../app-auth/services/patient.account.service";
import {IntlServicePP} from "../../intl/services/intl.service";
import {AuthProviderService} from "../../../app-auth/services/auth-provider.service";
import {SSOTokenService} from "../../services/sso.token.service";
import * as moment from "moment";

const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}$/;
const propraietrayToJson = function() {
  return moment(this).format('YYYY-MM-DD\\THH:mm:ss.SSS');
};

const datesParser = function reviver(key, value) {
  if (typeof value === "string" && dateFormat.test(value)) {
    return new Date(value);
  }
  return value;
};

export interface IPatientPortalMessage {
  action: string;
  data: any;
}

@Component({
  selector: 'app-web-form-container',
  templateUrl: './web.form.container.component.html',
  styleUrls: ['./web.form.container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebFormContainerComponent implements OnInit {
  @ViewChild('Iframe', {static: false}) Iframe: ElementRef;
  @Output() dataValidated = new EventEmitter();
  @Output() dataReceived = new EventEmitter();
  @Output() onLanguageChanged = new EventEmitter();
  @Input() loadCurrentPatient = false;

  public IFrameSrc: SafeResourceUrl;
  private webFormURL: string;
  private selectedLanguageKey: number;
  private patient: any;
  private webFormReady = false;
  private languageChanged = false;

  constructor(private sanitizer: DomSanitizer,
              private changeDetector: ChangeDetectorRef,
              private patientAccountSvc: PatientAccountService,
              private intlServicePP: IntlServicePP,
              private tokenProvider: AuthProviderService,
              private ssoTokenService: SSOTokenService) {
  }

  ngOnInit() {
    if (window.addEventListener) {
      window.addEventListener('message', this.receiveMessageFromIFrame.bind(this), false);
    } else {
      (window as any).attachEvent('onmessage', this.receiveMessageFromIFrame.bind(this));
    }
  }

  @Input()
  set URL(url: string) {
    this.webFormURL = url;
    this.intlServicePP.getUsedLanguageObservable().subscribe(lang => {
      if (lang) {
        if (this.selectedLanguageKey !== lang.languageKey) {
          // if web form already loaded get its data before reloading it
          if (this.webFormReady) {
            this.languageChanged = true;
            this.onLanguageChanged.emit();
            this.getData();
          } else {
            this.setUrl(this.webFormURL, lang.languageKey);
          }
          this.selectedLanguageKey = lang.languageKey;
        }
      }
    });
  }

  public setData(patient: any) {
    if (patient) {
      if (Date.prototype.toJSON !== propraietrayToJson){
        Date.prototype.toJSON = propraietrayToJson;
      }
      this.sendMessageToWebFormFrame({action: 'SET_PATIENT_DATA', data: JSON.stringify(patient.model.patient)});
    } else {
      this.sendMessageToWebFormFrame({action: 'CREATE_EMPTY_PATIENT', data: ''});
    }
  }

  public getData() {
      this.sendMessageToWebFormFrame({action: 'GET_PATIENT_DATA', data: ''});
  }

  private setUrl(url: string, languageKey: number) {
    if (this.tokenProvider.isAuthenticated()) {
      this.ssoTokenService.getSSOTokenForPatient().subscribe(token => {
        if (languageKey) {
          url = url + '&language=' + this.intlServicePP.usedLanguage.languageKey;
        }
        this.IFrameSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url.replace("{SSO_TOKEN}", token));
        this.changeDetector.markForCheck();
      });
    } else {
      this.ssoTokenService.getSSOToken().subscribe(token => {
        if (languageKey) {
          url = url + '&language=' + this.intlServicePP.usedLanguage.languageKey;
        }
        this.IFrameSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url.replace("{SSO_TOKEN}", token));
        this.changeDetector.markForCheck();
      });
    }
  }

  private receiveMessageFromIFrame(event: MessageEvent) {
    const iframeDocument = this.Iframe.nativeElement.contentWindow;
    if (event.source === iframeDocument) {
      if (event.data) {
        const message: IPatientPortalMessage = event.data;
        switch (message.action) {
          case 'READY':
            if (this.loadCurrentPatient) {
              this.sendMessageToWebFormFrame({action: 'LOAD_PORTAL_PATIENT', data: ''});
            } else {
              this.sendMessageToWebFormFrame({action: 'CREATE_EMPTY_PATIENT', data: ''});
            }
            break;
          case 'EMPTY_PATIENT_CREATED':
            this.webFormReady = true;
            if (this.patient) {
              // if web form was reloaded (e.g. as result of selected language change)
              this.setData(this.patient);
            }
            break;
          case 'PATIENT_DATA_VALIDATED':
            this.dataValidated.emit(message.data);
            break;
          case 'PATIENT_DATA_SENT':
            this.patient = JSON.parse(message.data, datesParser);
            if (this.languageChanged) {
              // if user changed selected language just reload the web form
              this.setUrl(this.webFormURL, this.selectedLanguageKey);
            } else {
              this.dataReceived.emit(this.patient);
            }
            this.languageChanged = false;
            break;
          default:
            break;
        }
      }
    }
  }

  private sendMessageToWebFormFrame(msg: IPatientPortalMessage) {
    const iframeDocument = this.Iframe.nativeElement.contentWindow;
    if (iframeDocument) {
      iframeDocument.postMessage(msg, '*');
    }
  }

}
